import { Injectable, NgZone } from '@angular/core';

/**
 * SelectEnhancerService — Globally replaces every native <select> in the
 * application with a styled custom-dropdown overlay.
 *
 * Why this exists: native <select> dropdown panels are drawn by the OS /
 * browser. On Chrome DevTools mobile preview they render as huge desktop
 * dropdowns that overflow the page width. On real phones they open as
 * picker wheels / Material modals which look fine, but the trigger itself
 * inherits inconsistent browser styling. Doing a per-page replacement
 * across 25+ HTML files is risky and tedious; this service does it once
 * at app boot and applies to every <select> the framework ever renders.
 *
 * How it works:
 *  1. On init we scan `document.body` for existing <select> elements and
 *     enhance each one.
 *  2. A MutationObserver watches for <select> elements added later (by
 *     Angular component renders, *ngIf toggles, route changes, etc.).
 *  3. For each enhanced <select> we:
 *       • Wrap it in a `<div class="sm-wrap">` (relative-positioned).
 *       • Hide the native <select> visually (opacity:0, inset:0,
 *         pointer-events:none, tabindex:-1) but keep it in the DOM so
 *         Angular's NgSelectControlValueAccessor / ngModel / Reactive
 *         Forms / (change) handlers still work — they read/write
 *         `select.value` and the native `change` event.
 *       • Append a styled trigger button + options panel as siblings.
 *       • Bidirectional sync:
 *           – Native change (programmatic or user) → updates trigger label.
 *           – Option click in panel → sets native value + dispatches
 *             `input` + `change` events.
 *  4. A nested MutationObserver on each select watches for <option>
 *     children changes (e.g. *ngFor updates) and re-renders the panel.
 *
 * Skip-attribute escape hatch:
 *   <select data-no-replace> ... </select>
 *   Used for the rare case where a select needs to stay native (e.g.
 *   inside an experimental form that needs the iOS picker wheel).
 *
 * Visual styling lives in `src/styles.scss` under the `.sm-*` namespace.
 */
@Injectable({ providedIn: 'root' })
export class SelectEnhancerService {
  private rootObserver: MutationObserver | null = null;
  private enhanced = new WeakSet<HTMLSelectElement>();
  private optionObservers = new WeakMap<HTMLSelectElement, MutationObserver>();
  /** Per-select cleanup hooks (event listeners). */
  private teardown = new WeakMap<HTMLSelectElement, () => void>();

  constructor(private zone: NgZone) {}

  /** Boot-time hook called via APP_INITIALIZER. Safe to call multiple times. */
  init(): void {
    if (this.rootObserver) return;

    // Run outside Angular's NgZone so MutationObserver callbacks don't
    // trigger unnecessary change-detection cycles. We hop back into the
    // zone only when we mutate Angular-bound state via dispatchEvent.
    this.zone.runOutsideAngular(() => {
      // 1. Initial scan — enhance any selects already in the DOM.
      document.querySelectorAll<HTMLSelectElement>('select').forEach(s => {
        this.enhance(s);
      });

      // 2. Watch for new selects added by future Angular renders.
      this.rootObserver = new MutationObserver(mutations => {
        for (const m of mutations) {
          m.addedNodes.forEach(n => this.scanNode(n));
          m.removedNodes.forEach(n => this.scanNodeForRemoval(n));
        }
      });
      this.rootObserver.observe(document.body, { childList: true, subtree: true });
    });
  }

  /** Recursively find <select> inside an added node. */
  private scanNode(n: Node): void {
    if (!(n instanceof HTMLElement)) return;
    if (n.tagName === 'SELECT') {
      this.enhance(n as HTMLSelectElement);
      return;
    }
    n.querySelectorAll?.<HTMLSelectElement>('select').forEach(s => this.enhance(s));
  }

  /** Cleanup overlays for selects that just got removed from DOM. */
  private scanNodeForRemoval(n: Node): void {
    if (!(n instanceof HTMLElement)) return;
    const selects: HTMLSelectElement[] = [];
    if (n.tagName === 'SELECT') selects.push(n as HTMLSelectElement);
    n.querySelectorAll?.<HTMLSelectElement>('select').forEach(s => selects.push(s));
    for (const s of selects) {
      // CRITICAL: only teardown if the select truly left the DOM.
      // Our own enhance() function MOVES the select from its original
      // parent into a new .sm-wrap, which fires a `removedNodes`
      // mutation on the original parent. Without this guard we would
      // immediately rip out the click listeners we just attached —
      // which is exactly why the dropdowns were not opening.
      // `Node.isConnected` is true while the node remains anywhere
      // in the document, false only when truly detached.
      if (s.isConnected) continue;
      this.teardown.get(s)?.();
      this.optionObservers.get(s)?.disconnect();
      this.enhanced.delete(s);
      this.teardown.delete(s);
      this.optionObservers.delete(s);
    }
  }

  /** Decorate a single native select with the styled overlay UI. */
  private enhance(select: HTMLSelectElement): void {
    if (this.enhanced.has(select)) return;
    if (select.hasAttribute('data-no-replace')) return;
    // Skip multi-selects — they need a totally different UI (chip list).
    if (select.multiple) return;
    // Skip if already wrapped (defensive — covers re-runs).
    if (select.parentElement?.classList.contains('sm-wrap')) return;

    this.enhanced.add(select);

    // ── Build wrapper + UI ───────────────────────────────────────
    const wrap = document.createElement('div');
    wrap.className = 'sm-wrap';

    // Preserve display-block-ness of the original select on form rows.
    select.parentNode!.insertBefore(wrap, select);
    wrap.appendChild(select);

    // Hide the native select but keep it accessible to Angular.
    select.classList.add('sm-native');
    select.tabIndex = -1;
    // Keep aria-hidden true so screen readers focus the trigger instead.
    select.setAttribute('aria-hidden', 'true');

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'sm-trigger';
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');
    if (select.disabled) trigger.disabled = true;

    const label = document.createElement('span');
    label.className = 'sm-label';

    const caret = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    caret.setAttribute('class', 'sm-caret');
    caret.setAttribute('viewBox', '0 0 12 8');
    caret.setAttribute('width', '12');
    caret.setAttribute('height', '8');
    caret.innerHTML =
      '<path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" fill="none"/>';

    trigger.appendChild(label);
    trigger.appendChild(caret);

    const panel = document.createElement('ul');
    panel.className = 'sm-panel';
    panel.setAttribute('role', 'listbox');
    panel.hidden = true;

    wrap.appendChild(trigger);
    wrap.appendChild(panel);

    // ── Helpers ──────────────────────────────────────────────────
    const renderOptions = () => {
      panel.innerHTML = '';
      Array.from(select.options).forEach((opt) => {
        const li = document.createElement('li');
        li.className = 'sm-option';
        if (opt.disabled) li.classList.add('sm-option-disabled');
        if (opt.value === '' && opt.text) li.classList.add('sm-option-placeholder');
        li.dataset['value'] = opt.value;
        li.dataset['index'] = String(opt.index);
        li.textContent = opt.text;
        if (opt.index === select.selectedIndex) li.classList.add('sm-selected');
        li.setAttribute('role', 'option');
        li.addEventListener('click', (e) => {
          e.stopPropagation();
          if (opt.disabled) return;
          // Run inside the Angular zone so ngModel / FormControl / (change)
          // bindings see the update.
          this.zone.run(() => {
            // Use selectedIndex (works for both string values and
            // Angular's internal "0: 1" hashed values for [ngValue] objects).
            select.selectedIndex = opt.index;
            // Fire BOTH events — Reactive Forms listens on `change`,
            // some host-binding listeners on `input`.
            select.dispatchEvent(new Event('input', { bubbles: true }));
            select.dispatchEvent(new Event('change', { bubbles: true }));
          });
          updateLabel();
          markSelected();
          closePanel();
        });
        panel.appendChild(li);
      });
      updateLabel();
    };

    const updateLabel = () => {
      const selected = select.options[select.selectedIndex];
      if (!selected || (selected.value === '' && selected.text === '')) {
        label.textContent = select.getAttribute('placeholder') || 'Select...';
        trigger.classList.add('sm-empty');
        return;
      }
      label.textContent = selected.text;
      // A blank string value with non-empty text (placeholder pattern) still
      // shows as "empty" so the muted-color treatment applies.
      if (selected.value === '') trigger.classList.add('sm-empty');
      else trigger.classList.remove('sm-empty');
    };

    const markSelected = () => {
      panel.querySelectorAll('.sm-option').forEach((el) => {
        const li = el as HTMLLIElement;
        const idx = Number(li.dataset['index']);
        li.classList.toggle('sm-selected', idx === select.selectedIndex);
      });
    };

    // Position the FIXED panel against the trigger's viewport rect so it escapes
    // any modal / scroll-container `overflow: hidden` (options were getting cut off).
    // Flip above the trigger when there isn't enough room below.
    const positionPanel = () => {
      const r = trigger.getBoundingClientRect();
      const gap = 4;
      panel.style.minWidth = r.width + 'px';
      panel.style.left = Math.max(8, r.left) + 'px';
      const desired = Math.min(panel.scrollHeight, 260);   // capped to .sm-panel max-height
      const below = window.innerHeight - r.bottom;
      if (below < desired + gap && r.top > below) {
        panel.style.top = 'auto';
        panel.style.bottom = (window.innerHeight - r.top + gap) + 'px';
      } else {
        panel.style.bottom = 'auto';
        panel.style.top = (r.bottom + gap) + 'px';
      }
    };

    const openPanel = () => {
      if (select.disabled) return;
      panel.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      wrap.classList.add('sm-open');
      positionPanel();
      // Keep the fixed panel glued to the trigger if anything scrolls or the
      // window resizes while it's open (capture:true also catches inner scrolls).
      window.addEventListener('scroll', positionPanel, true);
      window.addEventListener('resize', positionPanel);
      // Scroll selected into view if any.
      const selectedEl = panel.querySelector('.sm-selected') as HTMLElement | null;
      if (selectedEl) selectedEl.scrollIntoView({ block: 'nearest' });
    };

    const closePanel = () => {
      panel.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      wrap.classList.remove('sm-open');
      window.removeEventListener('scroll', positionPanel, true);
      window.removeEventListener('resize', positionPanel);
    };

    const togglePanel = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      if (panel.hidden) openPanel();
      else closePanel();
    };

    // Defensive — pointerdown/touchstart guarantee the click reaches the
    // trigger on touch devices even when an ancestor with `touch-action`
    // or `pointer-events: none` would otherwise swallow the click event.
    const onPointerDown = (e: Event) => {
      e.stopPropagation();
    };

    // Capture-phase doc click — fires BEFORE bubble-phase handlers. Used
    // for outside-click detection. We check `wrap.contains` so clicks
    // ON the trigger or panel are excluded.
    const onDocClick = (ev: MouseEvent) => {
      if (!panel.hidden && !wrap.contains(ev.target as Node)) closePanel();
    };

    const onEsc = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape' && !panel.hidden) closePanel();
    };

    const onNativeChange = () => {
      updateLabel();
      markSelected();
    };

    const onNativeDisabled = () => {
      trigger.disabled = select.disabled;
      if (select.disabled) closePanel();
    };

    trigger.addEventListener('click', togglePanel);
    trigger.addEventListener('pointerdown', onPointerDown);
    // Listen on capture so we beat any document-level stopPropagation calls
    // from other components that might suppress bubble-phase clicks.
    document.addEventListener('click', onDocClick, true);
    document.addEventListener('keydown', onEsc);
    select.addEventListener('change', onNativeChange);

    // Watch for `disabled` attribute toggles from Angular.
    const attrObserver = new MutationObserver((muts) => {
      for (const m of muts) {
        if (m.attributeName === 'disabled') onNativeDisabled();
      }
    });
    attrObserver.observe(select, { attributes: true });

    // Watch for option list changes (e.g. *ngFor inside the select).
    const optObserver = new MutationObserver(() => {
      renderOptions();
      markSelected();
    });
    optObserver.observe(select, { childList: true, subtree: true, characterData: true });
    this.optionObservers.set(select, optObserver);

    // TC-4489 (2026-11-03): Angular sets select.value directly, with no change event, so after a reset or an edit-form load the label could say UPI
    // while Cash was what would be saved. Follow every value set by code too.
    const refreshAfter = (name: 'value' | 'selectedIndex') => {
      const native = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, name)!;
      Object.defineProperty(select, name, {
        configurable: true,
        get: () => native.get!.call(select),
        set: (v: unknown) => { native.set!.call(select, v); updateLabel(); markSelected(); },
      });
    };
    refreshAfter('value');
    refreshAfter('selectedIndex');

    // Initial render.
    renderOptions();
    markSelected();

    this.teardown.set(select, () => {
      delete (select as any).value;
      delete (select as any).selectedIndex;
      trigger.removeEventListener('click', togglePanel);
      trigger.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('click', onDocClick, true);
      document.removeEventListener('keydown', onEsc);
      select.removeEventListener('change', onNativeChange);
      window.removeEventListener('scroll', positionPanel, true);
      window.removeEventListener('resize', positionPanel);
      attrObserver.disconnect();
    });
  }
}
